import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { BellRing, Users } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { toast } from '@/hooks/useToast';
import type { TranslationKey } from '@/types/i18n.types';
import { ILegalMemberStatus, LegalDocumentKey } from '@/lib/legal/legal.model';

const DOCUMENT_LABEL_KEY: Record<LegalDocumentKey, TranslationKey> = {
  cancellation_policy: 'legalSettings.documents.cancellationPolicy',
  privacy_policy: 'legalSettings.documents.privacyPolicy',
  terms_conditions: 'legalSettings.documents.termsConditions',
};

interface LegalMembersCardProps {
  members: ILegalMemberStatus[];
  onRequestUpdate: () => Promise<number>;
  isRequesting: boolean;
}

/**
 * Per l'agenzia: chi dei membri usa i testi dell'agenzia, chi li ha personalizzati e
 * chi e' rimasto indietro rispetto all'ultima versione. Da qui parte la richiesta di
 * aggiornamento, che arriva ai membri come notifica in-app.
 */
export const LegalMembersCard = ({ members, onRequestUpdate, isRequesting }: LegalMembersCardProps) => {
  const { t } = useI18n();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const outdatedCount = members.filter((m) => m.outdated.length > 0).length;

  const handleConfirm = async () => {
    try {
      const count = await onRequestUpdate();
      toast({
        variant: 'success',
        title: t('legalSettings.members.requestSent', { count }),
      });
      setConfirmOpen(false);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: t('legalSettings.members.requestError'),
        description: error?.message,
      });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          {t('legalSettings.members.title')}
        </CardTitle>
        <p className="text-sm text-gray-600">{t('legalSettings.members.description')}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {members.map((member) => (
            <li key={member.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
              <span className="text-sm font-medium text-gray-900">
                {member.name ?? t('notifications.fallback.user')}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {member.overridden.length === 0 ? (
                  <Badge variant="secondary">{t('legalSettings.members.allInherited')}</Badge>
                ) : (
                  member.overridden.map((key) => (
                    <Badge
                      key={key}
                      variant={member.outdated.includes(key) ? 'destructive' : 'outline'}
                    >
                      {t(DOCUMENT_LABEL_KEY[key])}
                      {member.outdated.includes(key) && ` · ${t('legalSettings.members.outdated')}`}
                    </Badge>
                  ))
                )}
              </div>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {t('legalSettings.members.outdatedSummary', { count: outdatedCount })}
          </p>
          <Button variant="outline" className="gap-2" onClick={() => setConfirmOpen(true)}>
            <BellRing className="h-4 w-4" />
            {t('legalSettings.members.requestUpdate')}
          </Button>
        </div>
      </CardContent>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t('legalSettings.members.requestUpdate')}
        size="sm"
      >
        <div className="space-y-4 px-6 py-4">
          <p className="text-sm text-gray-600">
            {t('legalSettings.members.confirmText', { count: members.length })}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConfirm} disabled={isRequesting}>
              {t('legalSettings.members.confirmButton')}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
};
